import moment from "moment";
import {
  InsurerReport,
  InsurerReportEntry,
  InsurerReportEntryMetadata,
  InsurerReportEntryMetadataMap,
  InsurerReportMetadata,
  InsurerReportMetadataMap,
} from "types/insurerReports";
import {
  getShortInsurerName,
  handleAxiosError,
  prepareNotificationObject,
} from "utils/utils";
import { matchesSearchTerm, normalizeSearchTerm } from "utils/searchMatch";
import { countMatchedInsurerReportEntries } from "pages/InsurerReports/insurerReportUtils";
import * as API from "../api/api";
import { StoreState } from "./InsurerReportsStore";
import { setNotificationMessage } from "./AppStoreActions";

// Master/detail page: `insurerReports` holds the report headers of the current
// query (no entries), `selectedInsurerReportId` the single selected report and
// `insurerReportEntries` the entries of that report only, loaded on selection.

export const setStoreValue =
  (name: keyof StoreState, value) =>
  ({ setState }) => {
    setState({
      [name]: value,
    });
  };

export const setStoreValues =
  (values) =>
  ({ setState }) => {
    setState({
      ...values,
    });
  };

// Row metadata carries the user's screen state (which report is selected and
// therefore has its entry list open). Operations must rebuild it from the
// previous value instead of from scratch, otherwise every write collapses the
// open list and drops the selection.
const buildReportMetadata = (
  report: InsurerReport,
  previous?: InsurerReportMetadata
): InsurerReportMetadata => ({
  selected: previous?.selected ?? false,
  visible: previous?.visible ?? true,
  deleted: report.isDeleted,
  inEditMode: false,
  changes: [],
  editable: false,
  oldRow: null,
});

// `visible` / `searchMatch` are derived by filterInsurerReportEntries (search term,
// perfect-match toggle), so they are left at their previous value here;
// `matched` / `highlighted` follow the row.
const buildEntryMetadata = (
  entry: InsurerReportEntry,
  previous?: InsurerReportEntryMetadata
): InsurerReportEntryMetadata => ({
  selected: previous?.selected ?? false,
  visible: previous?.visible ?? false,
  matched: entry.paymentId !== null || entry.paymentPlanEntryId !== null,
  highlighted: entry.isAutomaticMatch === true,
  searchMatch: previous?.searchMatch ?? true,
  inEditMode: false,
  changes: [],
  editable: false,
  oldRow: null,
});

// state of an empty detail pane (no report selected / selection dropped)
const noDetailState = {
  selectedInsurerReportId: null,
  insurerReportEntries: [],
  insurerReportEntryMetadataMap: {},
  insurerReportEntriesLoadStatus: "none",
};

export const hydrateInsurerReportEntries = (
  insurerReportEntries: InsurerReportEntry[]
): InsurerReportEntry[] =>
  (insurerReportEntries ?? [])
    .filter((entry) => entry.isDeleted === false)
    .map((entry) => ({
      ...entry,
      bookingDateFormatted: entry.bookingDate
        ? moment(entry.bookingDate).format("DD.MM.YYYY")
        : "",
      paymentDateFormatted: entry.paymentDate
        ? moment(entry.paymentDate).format("DD.MM.YYYY")
        : "",
    }));

export const deleteInsurerReport =
  (payload, onSuccess?, onError?) =>
  async ({ setState, dispatch }) => {
    setState({ deleteInsurerReportStatus: "loading" });
    try {
      const response = await API.deleteInsurerReport({
        insurerReportId: payload.insurerReportId,
      });
      setState({ deleteInsurerReportStatus: "success" });
      onSuccess && onSuccess(response.data);
    } catch (error: any) {
      handleAxiosError(error, (message) => {
        dispatch(
          setNotificationMessage(
            prepareNotificationObject("deleteInsurerReport", message)
          )
        );
      });
      setState({ deleteInsurerReportStatus: "error" });
      onError && onError();
    }
  };

export const matchInsurerReport =
  (payload, onSuccess?, onError?) =>
  async ({ setState, dispatch }) => {
    setState({ matchInsurerReportStatus: "loading" });
    try {
      const response = await API.matchInsurerReport({
        insurerReportId: payload.insurerReportId,
      });
      setState({ matchInsurerReportStatus: "success" });
      onSuccess && onSuccess(response.data);
    } catch (error: any) {
      handleAxiosError(error, (message) => {
        dispatch(
          setNotificationMessage(
            prepareNotificationObject("matchInsurerReport", message)
          )
        );
      });
      setState({ matchInsurerReportStatus: "error" });
      onError && onError();
    }
  };

export const importInsurerReport =
  (payload, onSuccess?, onError?) =>
  async ({ setState, dispatch }) => {
    setState({ importInsurerReportStatus: "loading" });
    try {
      await API.importInsurerReport({
        name: payload.name,
        base64String: payload.base64String,
        insurerId: payload.insurerId,
        grecoCompanyNumber: payload.grecoCompanyNumber,
        currencyCode: payload.currencyCode,
        insurerReportDate: payload.insurerReportDate,
      });
      setState({ importInsurerReportStatus: "success" });
      onSuccess && onSuccess();
    } catch (error: any) {
      handleAxiosError(error, (message) => {
        dispatch(
          setNotificationMessage(
            prepareNotificationObject("importInsurerReport", message)
          )
        );
      });
      setState({ importInsurerReportStatus: "error" });
      onError && onError();
    }
  };

export const matchInsurerReportEntry =
  (payload, onSuccess?, onError?) =>
  async ({ setState, dispatch }) => {
    setState({ matchInsurerReportEntryStatus: "loading" });
    try {
      const response = await API.matchInsurerReportEntryManually({
        insurerReportEntryId: payload.insurerReportEntryId,
        paymentId: payload.paymentId ?? null,
        paymentPlanEntryId: payload.paymentPlanEntryId ?? null,
      });
      setState({ matchInsurerReportEntryStatus: "success" });
      onSuccess && onSuccess(response.data);
    } catch (error: any) {
      handleAxiosError(error, (message) => {
        dispatch(
          setNotificationMessage(
            prepareNotificationObject("matchInsurerReportEntry", message)
          )
        );
      });
      setState({ matchInsurerReportEntryStatus: "error" });
      onError && onError();
    }
  };

export const getMatchedTargetInfo =
  (payload, onSuccess?, onError?) =>
  async ({ setState, getState, dispatch }) => {
    setState({ infoInsurerReportEntryStatus: "loading" });
    try {
      let target: any;
      let targetKind: "payment" | "paymentPlanEntry";
      // an entry matched to a payment carries that payment's paymentPlanEntryId as
      // well (both automatic and manual matching copy it), so the payment is the
      // matched target whenever paymentId is set; only then is it the payment plan entry
      if (payload.paymentId) {
        targetKind = "payment";
        // the entry DTO carries no clientId, so GET /payment/{clientId}/{paymentId}
        // cannot be used; query the payment by its id instead. The company numbers
        // are mandatory: the payment query rejects a request whose company list does
        // not intersect the user's permissions (403), and an empty list never does.
        const report = getState().insurerReports.find(
          (r: InsurerReport) => r.insurerReportId === payload.insurerReportId
        );
        const res = await API.paymentQuery({
          IncludeOnlyIds: [payload.paymentId],
          GrecoCompanyNumbers: report ? [report.grecoCompanyNumber] : [],
        });
        target = Array.isArray(res.data) ? res.data[0] : res.data;
        if (!target) {
          throw new Error(payload.t("pct.matchedPaymentNotFound.label"));
        }
      } else {
        targetKind = "paymentPlanEntry";
        const res = await API.getPaymentPlanEntry({
          paymentPlanEntryId: payload.paymentPlanEntryId,
        });
        target = res.data;
        if (!target) {
          throw new Error(payload.t("pct.matchedTargetNotFound.label"));
        }
      }
      setState({
        infoMatchedTarget: target,
        infoMatchedTargetKind: targetKind,
        infoInsurerReportEntryStatus: "success",
      });
      onSuccess && onSuccess(target);
    } catch (error: any) {
      handleAxiosError(error, (message) => {
        dispatch(
          setNotificationMessage(
            prepareNotificationObject("getMatchedTargetInfo", message)
          )
        );
      });
      setState({ infoInsurerReportEntryStatus: "error" });
      onError && onError();
    }
  };

// --- selection / detail loading -------------------------------------------

// Exclusive selection: exactly the given report is selected (null = none) and its
// entries are (re)loaded; the previous detail is dropped right away so a stale
// list is never shown under the new master row.
export const selectInsurerReport =
  (insurerReportId: number | null) =>
  ({ setState, getState, dispatch }) => {
    const previousMetadataMap: InsurerReportMetadataMap =
      getState().insurerReportMetadataMap;
    const insurerReportMetadataMap: InsurerReportMetadataMap = {};
    Object.keys(previousMetadataMap).forEach((id) => {
      insurerReportMetadataMap[id] = {
        ...previousMetadataMap[id],
        selected: Number(id) === insurerReportId,
      };
    });
    setState({
      ...noDetailState,
      insurerReportMetadataMap,
      selectedInsurerReportId: insurerReportId,
    });
    if (insurerReportId !== null) {
      dispatch(loadInsurerReportEntries(insurerReportId));
    }
  };

export const loadInsurerReportEntries =
  (insurerReportId: number) =>
  async ({ setState, getState, dispatch }) => {
    setState({ insurerReportEntriesLoadStatus: "loading" });
    try {
      const response = await API.getInsurerReportEntries({ insurerReportId });
      // the user moved on to another report while this request was in flight
      if (getState().selectedInsurerReportId !== insurerReportId) return;

      const insurerReportEntries = hydrateInsurerReportEntries(response.data);
      const insurerReportEntryMetadataMap: InsurerReportEntryMetadataMap = {};
      insurerReportEntries.forEach((entry) => {
        insurerReportEntryMetadataMap[entry.insurerReportEntryId] =
          buildEntryMetadata(entry);
      });
      setState({
        insurerReportEntries,
        insurerReportEntryMetadataMap,
        insurerReportEntriesLoadStatus: "success",
      });
    } catch (error: any) {
      if (getState().selectedInsurerReportId !== insurerReportId) return;
      handleAxiosError(error, (message) => {
        dispatch(
          setNotificationMessage(
            prepareNotificationObject("loadInsurerReportEntries", message)
          )
        );
      });
      setState({ insurerReportEntriesLoadStatus: "error" });
    }
  };

// --- targeted updates after a write -------------------------------------
// Every mutation endpoint returns the affected rows in the same shape as the
// query, so the store is patched in place instead of re-running the whole
// query. Same pattern as the bank statement entry operations.

// counters of the master row recomputed from the loaded entries of the selected report
const withRecountedSelectedReport = (
  insurerReports: InsurerReport[],
  selectedInsurerReportId: number | null,
  insurerReportEntries: InsurerReportEntry[]
): InsurerReport[] => {
  if (selectedInsurerReportId === null) return insurerReports;
  const counts = countMatchedInsurerReportEntries(insurerReportEntries);
  return insurerReports.map((report) =>
    report.insurerReportId === selectedInsurerReportId
      ? { ...report, ...counts }
      : report
  );
};

// single entry after a manual match / unmatch (the entry belongs to the selected report)
export const applyInsurerReportEntry =
  (insurerReportEntry: InsurerReportEntry) =>
  ({ setState, getState }) => {
    const [entry] = hydrateInsurerReportEntries([insurerReportEntry]);
    if (!entry) return;
    const insurerReportEntries = getState().insurerReportEntries.map((el) =>
      el.insurerReportEntryId === entry.insurerReportEntryId ? entry : el
    );
    setState({
      insurerReportEntries,
      insurerReportEntryMetadataMap: {
        ...getState().insurerReportEntryMetadataMap,
        [entry.insurerReportEntryId]: buildEntryMetadata(
          entry,
          getState().insurerReportEntryMetadataMap[entry.insurerReportEntryId]
        ),
      },
      insurerReports: withRecountedSelectedReport(
        getState().insurerReports,
        getState().selectedInsurerReportId,
        insurerReportEntries
      ),
    });
  };

// one report (with all of its entries) after an automatic rematch: the master row
// is replaced; the entries are taken over only when the report is the selected one
export const applyInsurerReport =
  (payload) =>
  ({ setState, getState }) => {
    const { insurerReport, taxonomy, grecoCompanies, t } = payload;
    if (!insurerReport) return;

    const [report] = hydrateInsurerReports({
      insurerReports: [insurerReport],
      taxonomy,
      grecoCompanies,
      t,
    });
    delete report.insurerReportEntries;

    const patch: Partial<StoreState> = {
      insurerReports: getState().insurerReports.map((el) =>
        el.insurerReportId === report.insurerReportId ? report : el
      ),
      insurerReportMetadataMap: {
        ...getState().insurerReportMetadataMap,
        [report.insurerReportId]: buildReportMetadata(
          report,
          getState().insurerReportMetadataMap[report.insurerReportId]
        ),
      },
    };

    if (report.insurerReportId === getState().selectedInsurerReportId) {
      const entries = hydrateInsurerReportEntries(
        insurerReport.insurerReportEntries
      );
      const previousEntryMetadataMap = getState().insurerReportEntryMetadataMap;
      const insurerReportEntryMetadataMap: InsurerReportEntryMetadataMap = {};
      entries.forEach((entry) => {
        insurerReportEntryMetadataMap[entry.insurerReportEntryId] =
          buildEntryMetadata(
            entry,
            previousEntryMetadataMap[entry.insurerReportEntryId]
          );
      });
      patch.insurerReportEntries = entries;
      patch.insurerReportEntryMetadataMap = insurerReportEntryMetadataMap;
      patch.insurerReportEntriesLoadStatus = "success";
    }

    setState(patch);
  };

// deletion removes every report imported from the same document, so the
// endpoint answers with all of them
export const removeInsurerReports =
  (deletedInsurerReports) =>
  ({ setState, getState }) => {
    const deletedIds = new Set(
      (deletedInsurerReports ?? []).map(
        (report: InsurerReport) => report.insurerReportId
      )
    );
    if (deletedIds.size === 0) return;

    const insurerReportMetadataMap = {
      ...getState().insurerReportMetadataMap,
    };
    deletedIds.forEach((id: any) => delete insurerReportMetadataMap[id]);

    setState({
      insurerReports: getState().insurerReports.filter(
        (report: InsurerReport) => !deletedIds.has(report.insurerReportId)
      ),
      insurerReportMetadataMap,
      // the detail belongs to the selected report; drop it when that report is gone
      ...(deletedIds.has(getState().selectedInsurerReportId)
        ? noDetailState
        : {}),
    });
  };

// --- client-side search ---------------------------------------------------

// master grid: the search box of the menubar matches the report columns only
// (the entries are not on the client); a selected report that falls out of the
// result is deselected so the detail pane does not show an invisible report
export const filterInsurerReports =
  (payload) =>
  ({ setState, getState, dispatch }) => {
    const term = normalizeSearchTerm(payload.searchTerm);
    const insurerReportMetadataMap: InsurerReportMetadataMap = {
      ...getState().insurerReportMetadataMap,
    };
    let selectedReportHidden = false;

    getState().insurerReports.forEach((report: InsurerReport) => {
      const meta = insurerReportMetadataMap[report.insurerReportId];
      if (!meta) return;
      const visible = matchesSearchTerm(
        [
          report.insurerReportNumber,
          report.insurerName,
          report.companyName,
          report.currencyName,
        ],
        term
      );
      insurerReportMetadataMap[report.insurerReportId] = { ...meta, visible };
      if (
        !visible &&
        report.insurerReportId === getState().selectedInsurerReportId
      ) {
        selectedReportHidden = true;
      }
    });

    setState({ insurerReportMetadataMap });
    if (selectedReportHidden) dispatch(selectInsurerReport(null));
  };

// detail grid: search box of the detail pane + perfect-match toggle decide which
// entries of the selected report are visible (text match on client code, client
// name and policy numbers)
export const filterInsurerReportEntries =
  (payload: { entriesSearchTerm: string; includePerfectMatches: boolean }) =>
  ({ setState, getState }) => {
    const term = normalizeSearchTerm(payload.entriesSearchTerm);
    const insurerReportEntryMetadataMap: InsurerReportEntryMetadataMap = {
      ...getState().insurerReportEntryMetadataMap,
    };

    getState().insurerReportEntries.forEach((entry: InsurerReportEntry) => {
      const meta = insurerReportEntryMetadataMap[entry.insurerReportEntryId];
      if (!meta) return;
      const searchMatch = matchesSearchTerm(
        [
          entry.clientCode,
          entry.clientName,
          entry.policyNumber,
          entry.policyNumberPaymentPlanEntry,
          entry.policySubNumber,
        ],
        term
      );
      // Perfect (automatic) matches are the orange rows; the filter bar toggles them.
      const perfectMatchAllowed =
        payload.includePerfectMatches || entry.isAutomaticMatch !== true;
      insurerReportEntryMetadataMap[entry.insurerReportEntryId] = {
        ...meta,
        searchMatch,
        visible: entry.isDeleted === false && searchMatch && perfectMatchAllowed,
      };
    });

    setState({ insurerReportEntryMetadataMap });
  };

export const hydrateInsurerReports = ({
  insurerReports,
  taxonomy,
  grecoCompanies,
  t,
}) => {
  return insurerReports.map((report: InsurerReport) => {
    return {
      ...report,
      insurerName:
        getShortInsurerName(report.insurerId + "") !== ""
          ? getShortInsurerName(report.insurerId + "")
          : report.insurerName // insurer name from CRM, for insurers without a short name
          ? report.insurerName
          : String(report.insurerId ?? ""),
      currencyName: taxonomy?.Currency?.byId[report["currencyCode"]]
        ? t(taxonomy.Currency.byId[report["currencyCode"]].code)
        : String(report["currencyCode"] ?? ""),
      companyName:
        grecoCompanies?.find(
          (gc) => gc.companyNumber === report.grecoCompanyNumber
        )?.companyShort ?? String(report.grecoCompanyNumber ?? ""),
      insurerReportDateFormatted: report.insurerReportDate
        ? moment(report.insurerReportDate).format("DD.MM.YYYY")
        : "",
      insurerReportPaymentDateFormatted: report.insurerReportPaymentDate
        ? moment(report.insurerReportPaymentDate).format("DD.MM.YYYY")
        : "",
    };
  });
};

// master query: report headers only. A selection that is still part of the new
// result survives (and its entries are reloaded), otherwise the detail is cleared.
export const loadInsurerReports =
  (payload, onSuccess?) =>
  async ({ setState, getState, dispatch }) => {
    try {
      const { updatedGrecoCompanies, grecoCompanies, taxonomy } = payload;
      if (getState().insurerReportsLoadStatus === "loading") return;
      setState({
        insurerReportsLoadStatus: "loading",
      });

      const searchInsurersSelectedItem = getState().searchInsurersSelectedItem;
      const insurerIds = searchInsurersSelectedItem?.value?.id
        ? [searchInsurersSelectedItem.value.id]
        : [];

      const response = await API.insurerReportQuery({
        From: getState().fromDateFilter
          ? moment(getState().fromDateFilter).format("YYYY-MM-DD")
          : null,
        To: getState().toDateFilter
          ? moment(getState().toDateFilter).format("YYYY-MM-DD")
          : null,
        CompanyRegisterNumbers: updatedGrecoCompanies
          .filter((el) => el.selected)
          .map((el) => el.companyRegisterNumber),
        InsurerIds: insurerIds,
        DateFilterField: getState().searchDateFilterField,
      });

      let insurerReports: InsurerReport[] = response.data.filter(
        (el) => el.isDeleted === false
      );

      insurerReports = hydrateInsurerReports({
        insurerReports,
        grecoCompanies,
        taxonomy,
        t: payload.t,
      });

      const selectedInsurerReportId = getState().selectedInsurerReportId;
      const selectionSurvives =
        selectedInsurerReportId !== null &&
        insurerReports.some(
          (report) => report.insurerReportId === selectedInsurerReportId
        );

      // rows the user still has on screen keep their visibility / selection state
      const previousReportMetadataMap = getState().insurerReportMetadataMap;
      const insurerReportMetadataMap: InsurerReportMetadataMap = {};
      insurerReports.forEach((report) => {
        const meta = buildReportMetadata(
          report,
          previousReportMetadataMap[report.insurerReportId]
        );
        meta.selected =
          selectionSurvives &&
          report.insurerReportId === selectedInsurerReportId;
        insurerReportMetadataMap[report.insurerReportId] = meta;
        delete report.insurerReportEntries;
      });

      setState({
        insurerReports,
        insurerReportMetadataMap,
        insurerReportsLoadStatus: "success",
        ...(selectionSurvives ? {} : noDetailState),
      });
      if (selectionSurvives) {
        dispatch(loadInsurerReportEntries(selectedInsurerReportId));
      }
      onSuccess && onSuccess(response.data);
    } catch (error: any) {
      if (error.code !== "ERR_CANCELED") {
        handleAxiosError(error, (message) => {
          dispatch(
            setNotificationMessage(
              prepareNotificationObject("loadInsurerReports", message)
            )
          );
        });
        setState({
          insurerReportsLoadStatus: "error",
        });
      }
    }
  };
